import React, { useEffect, useRef } from 'react';
import {
    X,
    Linkedin,
    Twitter,
    Instagram,
    Facebook,
} from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import './SocialPreviewModal.css';

interface SocialContentItem {
    task_id: string;
    title: string;
    source_article?: string;
    synthesized_result?: string | null;
    approval_status?: string | null;
    source_hero_image_url?: string | null;
}

interface SocialPreviewModalProps {
    item: SocialContentItem;
    onClose: () => void;
}

type PublishablePlatform = 'linkedin' | 'twitter' | 'instagram' | 'facebook';

const PLATFORM_ORDER: PublishablePlatform[] = ['linkedin', 'twitter', 'instagram', 'facebook'];

const PLATFORM_LABELS: Record<PublishablePlatform, string> = {
    linkedin: 'LinkedIn',
    twitter: 'Twitter / X',
    instagram: 'Instagram',
    facebook: 'Facebook',
};

const PLATFORM_ICONS: Record<PublishablePlatform, React.ReactNode> = {
    linkedin: <Linkedin size={15} />,
    twitter: <Twitter size={15} />,
    instagram: <Instagram size={15} />,
    facebook: <Facebook size={15} />,
};

/** Extract post text from a platform value that may be a string or {post, cta, hashtags, ...} object. */
function extractPlatformText(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        const parts: string[] = [];
        if (value.post) parts.push(value.post);
        if (value.cta) parts.push(value.cta);
        if (value.hashtags) parts.push(value.hashtags);
        return parts.join('\n\n');
    }
    return String(value);
}

/** Resolve hero URL from a platform entry's hero_image object, with fallback. */
function resolveHeroUrl(platformEntry: any, fallbackUrl?: string | null): string | undefined {
    if (platformEntry?.hero_image?.urls?.large2x) return platformEntry.hero_image.urls.large2x;
    if (platformEntry?.hero_image?.urls?.large) return platformEntry.hero_image.urls.large;
    return fallbackUrl || undefined;
}

const SocialPreviewModal: React.FC<SocialPreviewModalProps> = ({ item, onClose }) => {
    const sourceTitle = item.source_article || item.title;

    // Parse synthesized_result once
    let parsed: Record<string, any> = {};
    try {
        if (item.synthesized_result) {
            parsed = JSON.parse(item.synthesized_result);
        }
    } catch {
        parsed = {};
    }

    // Escape key to close
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const overlayMouseDownTarget = useRef<EventTarget | null>(null);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && overlayMouseDownTarget.current === e.currentTarget) onClose();
    };

    // Resolve approval badge label
    const approvalLabel = item.approval_status === 'approved'
        ? 'Approved'
        : item.approval_status === 'pending'
            ? 'Pending'
            : item.approval_status
                ? item.approval_status.charAt(0).toUpperCase() + item.approval_status.slice(1)
                : 'Not reviewed';

    const approvalClass = item.approval_status === 'approved'
        ? 'social-preview-approval-badge approved'
        : 'social-preview-approval-badge pending';

    return (
        <div className="asset-editor-overlay" onMouseDown={e => { overlayMouseDownTarget.current = e.target; }} onClick={handleOverlayClick}>
            <div className="asset-editor-modal social-preview-modal">
                {/* Header */}
                <div className="social-preview-modal-header">
                    <div>
                        <h3 className="social-preview-modal-title">Social Media Posts</h3>
                        {sourceTitle && (
                            <div className="social-preview-modal-source">{sourceTitle}</div>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={approvalClass}>{approvalLabel}</span>
                        <GlassButton
                            variant="ghost"
                            size="sm"
                            icon={<X size={16} />}
                            onClick={onClose}
                            title="Close"
                        />
                    </div>
                </div>

                {/* Platform sections */}
                {PLATFORM_ORDER.map(p => {
                    const entry = parsed[p];
                    const text = extractPlatformText(entry);
                    if (!text) return null;
                    const heroUrl = resolveHeroUrl(entry, item.source_hero_image_url);

                    return (
                        <div
                            key={p}
                            className={`social-preview-platform-section platform-section-${p}`}
                            data-platform={p}
                        >
                            <div className="social-preview-platform-badge">
                                {PLATFORM_ICONS[p]}
                                <span>{PLATFORM_LABELS[p]}</span>
                            </div>
                            {heroUrl && (
                                <img
                                    src={heroUrl}
                                    alt={`${PLATFORM_LABELS[p]} hero`}
                                    className="social-preview-platform-hero"
                                />
                            )}
                            <p className="social-preview-platform-text">{text}</p>
                        </div>
                    );
                })}

                {/* Footer */}
                <div className="social-preview-footer">
                    <GlassButton variant="secondary" size="sm" onClick={onClose}>
                        Close
                    </GlassButton>
                </div>
            </div>
        </div>
    );
};

export default SocialPreviewModal;
